import React, { useState, useEffect, useRef } from "react";
import {Box, Collapse, Input, useBreakpointValue, useToast} from "@chakra-ui/react";
import MessageList from "./MessageList";
import InputArea from "./InputArea";
import ChatHistory from "./ChatHistory/ChatHistory";
import CampaignSummary from "./CampaignSummary";
import { streamAPI, cqAPI } from "../../lib/Auth";
import "./ChatComponent.css";
import _ from "lodash";
import {useLocation, useNavigate} from "react-router-dom";

function fixPartialHtml(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  if (doc.querySelector("parsererror")) {
    const openTags = htmlString.match(/<[^\/>]+>/g) || [];
    const closeTags = htmlString.match(/<\/[^>]+>/g) || [];

    if (openTags.length !== closeTags.length) {
      for (let i = openTags.length - 1; i >= 0; i--) {
        const tag = openTags[i].match(/<(\w+)/)[1];
        if (!htmlString.includes(`</${tag}>`)) {
          htmlString += `</${tag}>`;
        }
      }
    }
  }

  return htmlString;
}

function ChatComponent({
                         chatContext,
                         setChatContext,
                         chatSessionId,
                         setChatSessionId,
                         selectedBusiness,
                       }) {
  const [messages, setMessages] = useState([]);
  const [questionText, setQuestionText] = useState("");
  const [promptSuggestions, setPromptSuggestions] = useState([]);
  const [longProcessing, setLongProcessing] = useState(false);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [newSessionCreated, setNewSessionCreated] = useState(false);
  const abortControllerRef = useRef(null);
  const prevDependencyRef = useRef(null);
  const newButUnusedSessions = useRef({});
  const [chatHistory, setChatHistory] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true); // Add this line
  const isMobile = useBreakpointValue({ base: true, md: false });
  const fileInputRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignSummary, setShowCampaignSummary] = useState(false);

  const getNewChatSession = () => {
    return cqAPI
      .post("/session", {
        type: chatContext.type,
        ...(chatContext.for ? { for: chatContext.for } : {}),
      })
      .then((res) => res.data);
  };

  useEffect(() => {
    setLongProcessing(false);
    const newSession = async (sessionIndex) => {
      setDisableSendButton(true);
      newButUnusedSessions.current[sessionIndex] = {
        loading: true,
        started: Date.now(),
      };
      const session = await getNewChatSession();

      if (newButUnusedSessions.current[sessionIndex].loading) {
        newButUnusedSessions.current[sessionIndex] = session;
      }

      if (prevDependencyRef.current && prevDependencyRef.current.type) {
        const currentSessionIndex =
          `${prevDependencyRef.current.type}${
            selectedBusiness && selectedBusiness.bid
          }` +
          (prevDependencyRef.current.for
            ? `:${prevDependencyRef.current.for}`
            : "");
        if (currentSessionIndex === sessionIndex && session) {
          setChatSessionId(session.session_id);
          setPromptSuggestions(session.prompt_suggestions);
          setDisableSendButton(false);
        }
      }
    };

    if (chatContext.type && !_.isEqual(prevDependencyRef.current, chatContext)) {
      setQuestionText("");
      if (!chatContext.history) {
        const sessionIndex =
          `${chatContext.type}${selectedBusiness && selectedBusiness.bid}` +
          (chatContext.for ? `:${chatContext.for}` : "");
        setPromptSuggestions([]);
        setMessages([]);
        if (newButUnusedSessions.current[sessionIndex]) {
          const session = newButUnusedSessions.current[sessionIndex];
          if (session.loading) {
            if (session.started && Date.now() - session.started < 20000) {
              setDisableSendButton(true);
              return;
            }
            newButUnusedSessions.current[sessionIndex] = undefined;
            newSession(sessionIndex);
            return;
          }
          setChatSessionId(session.session_id);
          setPromptSuggestions(session.prompt_suggestions);
          setDisableSendButton(false);
        } else {
          newSession(sessionIndex);
        }
      } else {
        setDisableSendButton(false);
        setPromptSuggestions([]);
      }
    } else {
      setPromptSuggestions([]);
      setDisableSendButton(false);
    }
    prevDependencyRef.current = chatContext;
  }, [chatContext, selectedBusiness]);

  const currentServerChunks = useRef([]);
  const chatBoxRef = useRef(null);
  const longProcessingTimerRef = useRef(null);

  const getSession = async (sessionId) => cqAPI
    .get(`/sessions?session_id=${sessionId}`);

  const processReadableStream = async (reader, sessionId) => {
    setLongProcessing(false);
    while (true) {
      longProcessingTimerRef.current = setTimeout(() => {
        setLongProcessing(true);
      }, 3000);
      const { done, value } = await reader.read();
      clearTimeout(longProcessingTimerRef.current);
      longProcessingTimerRef.current = null;
      setLongProcessing(false);

      if (done) {
        await getSession(sessionId).then(sessionData => {
          const entries = sessionData?.data ?? [];
          const lastEntry = entries.length ? entries[entries.length - 1] : null;
          const qas = lastEntry?.qa ?? [];
          const lastQa = qas.length ? qas[qas.length - 1] : null;
          setMessages((prevMessages) => {
            let updatedMessages = [...prevMessages];
            const lastMsg = updatedMessages[updatedMessages.length - 1];
            if (!lastMsg) return updatedMessages;
            const structuredTypes = ["action_confirm", "deep_link", "job_created", "progress"];
            if (structuredTypes.includes(lastMsg.type)) {
              // Structured message is last — find the preceding server message to attach r_buffers
              for (let i = updatedMessages.length - 2; i >= 0; i--) {
                if (updatedMessages[i].type === "server") {
                  updatedMessages[i] = {
                    ...updatedMessages[i],
                    r_buffers: lastQa?.r_buffers,
                  };
                  break;
                }
              }
              return updatedMessages;
            }
            updatedMessages[updatedMessages.length - 1] = {
              type: "server",
              content: lastMsg.content ?? lastQa?.r_text ?? '',
              r_buffers: lastQa?.r_buffers,
            };
            return updatedMessages;
          });
        }).catch(err => console.error('cannot retrieve session info:', err));

        // Fetch campaigns after stream completes
        fetchSessionCampaigns(sessionId).then(fetchedCampaigns => {
          setCampaigns(fetchedCampaigns);
        });

        break;
      }

      let chunkText = new TextDecoder().decode(value);

      // Check if this chunk is a JSON message from the orchestrator (new types)
      const trimmed = chunkText.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.type === "action_confirm" || parsed.type === "deep_link"
              || parsed.type === "job_created" || parsed.type === "progress") {
            // Add as a structured message instead of raw text
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              const lastMsg = updatedMessages[updatedMessages.length - 1];
              // If last message is loading or empty server, replace it
              if (lastMsg && (lastMsg.type === "loading" || (lastMsg.type === "server" && !lastMsg.content))) {
                updatedMessages[updatedMessages.length - 1] = { type: parsed.type, content: parsed.content };
              } else {
                updatedMessages.push({ type: parsed.type, content: parsed.content });
              }
              return updatedMessages;
            });
            // Clear text buffer so subsequent chunks don't duplicate prior content
            currentServerChunks.current = [];
            continue;
          }
          if (parsed.type === "cancelled") {
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              updatedMessages[updatedMessages.length - 1] = {
                type: "server",
                content: "Request cancelled.",
              };
              return updatedMessages;
            });
            break;
          }
        } catch (_) {
          // Not valid JSON — treat as regular text chunk
        }
      }

      chunkText = chunkText.replace(/(```html|```)/g, "");
      chunkText = fixPartialHtml(chunkText);
      currentServerChunks.current.push(chunkText);

      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        const lastMsg = updatedMessages[updatedMessages.length - 1];
        const structuredTypes = ["action_confirm", "deep_link", "job_created", "progress"];
        if (lastMsg && structuredTypes.includes(lastMsg.type)) {
          // Don't overwrite structured messages — start a new server message
          updatedMessages.push({
            type: "server",
            content: currentServerChunks.current.join(""),
          });
        } else {
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMsg,
            type: "server",
            content: currentServerChunks.current.join(""),
          };
        }
        return updatedMessages;
      });
    }
  };

  const handleUserInput = async (input) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { type: "user", content: input },
    ]);

    currentServerChunks.current = [];
    setMessages((prevMessages) => [
      ...prevMessages,
      { type: "loading", content: null },
    ]);

    try {
      const newSession = messages.length === 0;

      setDisableSendButton(true);
      setIsStreaming(true);

      // Create AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await streamAPI.post("/answer", {
        text: input,
        session_id: chatSessionId,
        for: chatContext.for,
        type: chatContext.type,
      }, { signal: controller.signal });

      const reader = response.body.getReader();
      await processReadableStream(reader, chatSessionId);
      if (newSession) {
        const sessionIndex = `${chatContext.type}${
          selectedBusiness && selectedBusiness.bid
        }` + (chatContext.for ? `:${chatContext.for}` : "");
        setNewSessionCreated(!newSessionCreated);
        if (newButUnusedSessions.current[sessionIndex]) {
          newButUnusedSessions.current[sessionIndex] = undefined;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Request aborted by user");
        setMessages((prevMessages) => {
          let updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            type: "server",
            content: "Request cancelled.",
          };
          return updatedMessages;
        });
      } else {
        console.error("Error making API call:", error);
        setMessages((prevMessages) => {
          let updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            type: "server",
            content: "Error occurred while fetching data.",
          };
          return updatedMessages;
        });
      }
    } finally {
      setDisableSendButton(false);
      setIsStreaming(false);
      setLongProcessing(false);
      abortControllerRef.current = null;
    }
  };

  const handleStopRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Also notify the API (best-effort — the abort already closes the connection)
      cqAPI.post("/answer/stop", { session_id: chatSessionId }).catch(() => {});
    }
    if (longProcessingTimerRef.current) {
      clearTimeout(longProcessingTimerRef.current);
      longProcessingTimerRef.current = null;
    }
    setLongProcessing(false);
  };

  useEffect(() => {
    const selectedPath = location.pathname.split("/").filter((x) => !!x);
    if (selectedPath[0] === "history" && selectedPath[1] !== chatSessionId) {
      setChatSessionId(selectedPath[1]);
      getSession(selectedPath[1])
        .then((x) => x.data[0])
        .then((session) => {
          if (!session) return;
          setChatContext({
            type: session.type,
            name: session.patient_name,
            history: true,
            ...(session.type_id ? { [session.type_id]: session.type_id_value } : {}),
          });
          
          const messages = session.qa.map((msg) => [
            { type: "user", content: msg.q_text },
            { type: "server", content: msg.r_text, r_buffers: msg.r_buffers ?? [] },
          ]);
          setMessages(messages.flat());
        });
    }
  }, [location.pathname]);

  async function fetchChatHistory() {
    if (!chatContext.type) {
      setChatHistory([]);
      setIsLoading(false); // Stop loading if there's no chat context type
      return;
    }
    setIsLoading(true); // Start loading
    try {
      const res = await cqAPI.get("/sessions", {
        count: 30,
        type: chatContext?.type,
        ...(chatContext.for ? { for: chatContext.for } : {})
      });
      if (res.type === 'ok') {
        setChatHistory(res.data);
      }
    } catch (err) {
      console.log('Error retrieving session history', err);
    } finally {
      setIsLoading(false); // Ensure loading ends on success or error
    }
  }

  useEffect(() => {
    fetchChatHistory(); // Call the function directly
  }, [chatContext]); // Depend on `chatContext` to trigger the effect when it changes

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const isSessionCompleted = async (runNumber = 0) => {
    const s = await getSession(chatSessionId).then((x) => x.data[0]);
    if (s.conversation_label) return true;
    if (runNumber > 10) return false;
    await wait(2000);
    return isSessionCompleted(runNumber + 1);
  };

  useEffect(() => {
    if (!chatSessionId || !newSessionCreated) return;
    isSessionCompleted().then((x) => {
      if (!x) return;
      cqAPI
        .get("/sessions", {
          count: 30,
          type: chatContext?.type,
          ...(chatContext.for ? {for: chatContext.for} : {})
        })
        .then((res) => {
          if (res.type === 'ok') {
            setChatHistory(res.data);
          }
          const newSearchParams = new URLSearchParams(window.location.search);
          navigate(`/history/${chatSessionId}?${newSearchParams.toString()}`);
        });
    });
  }, [newSessionCreated]);

  const uploadCSVFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleCSVFileUpload = async (event) => {
    if (!chatSessionId) {
      return;
    }
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file);
      console.log(`Uploaded file ${file.name}`);
      try {
        const fileName = `private/${file.name}`;
        const params = new URLSearchParams({
          acl: 'private',
          name: fileName
        });

        const response = await cqAPI.post(`/upload?${params.toString()}`, formData);
        if (response.type === "ok") {
          const downloadParams = new URLSearchParams({
            id: fileName
          });
          uploadedFiles.push({
            fileName: file.name,
            url: `/file?${downloadParams.toString()}`,
            type: 'csv-file',
          });

        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      try {
        // Add loading message for the stream response
        currentServerChunks.current = [];
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: "loading", content: null },
        ]);
        setDisableSendButton(true);

        const attachResponse = await streamAPI.post(`/attachAnswerFile`, {
          session_id: chatSessionId,
          uploadedFiles
        });

        const reader = attachResponse.body.getReader();
        await processReadableStream(reader, chatSessionId);
        setDisableSendButton(false);
        toast({
          title: "File(s) uploaded successfully.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });

      } catch (error) {
        console.error("Error attaching file:", error);
        setMessages((prevMessages) => {
          let updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            type: "server",
            content: "Error occurred while processing file.",
          };
          return updatedMessages;
        });
        setDisableSendButton(false);
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    }
    setFileInputKey(Date.now());
  };

  const fetchSessionCampaigns = async (sessionId) => {
    try {
      const response = await cqAPI.get("/campaigns/by-session", { session_id: sessionId });
      if (response.type === "ok") {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching session campaigns:", error);
      return [];
    }
  };

  const handleNextClick = async () => {
    const fetchedCampaigns = await fetchSessionCampaigns(chatSessionId);
    if (fetchedCampaigns.length > 0) {
      setCampaigns(fetchedCampaigns);
      setShowCampaignSummary(true);
    }
  };

  const handleCampaignUpdate = (campaignId, newContent, type) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign._id !== campaignId) return campaign;
        const campaignType = type || campaign.type;
        if (campaignType === "phone" && typeof newContent === "object") {
          const updatedPhone = { ...campaign.template?.phone };
          Object.entries(newContent).forEach(([key, value]) => {
            updatedPhone[key] = { ...updatedPhone[key], text: value };
          });
          return {
            ...campaign,
            template: { ...campaign.template, phone: updatedPhone },
          };
        }
        if (campaignType === "sms") {
          return {
            ...campaign,
            template: {
              ...campaign.template,
              sms: { ...campaign.template?.sms, template: newContent },
            },
          };
        }
        if (campaignType === "email") {
          return {
            ...campaign,
            template: {
              ...campaign.template,
              email: { ...campaign.template?.email, email_content: newContent },
            },
          };
        }
        return campaign;
      })
    );
  };

  const handleCampaignBack = () => {
    setShowCampaignSummary(false);
  };

  const handleScheduleSuccess = () => {
    // Reset the campaign summary after successful scheduling
    setShowCampaignSummary(false);
    setCampaigns([]);
  };

  const handleSendTest = async (testData) => {
    const { processDate, campaigns: campaignsToTest } = testData;

    try {
      for (const campaign of campaignsToTest) {
        const response = await cqAPI.post("/campaigns/send?confirm=true", {
          campaign: {
            ...campaign,
            type: campaign.type,
            purpose: campaign.purpose || "campaign-manual",
          },
          process_date: processDate.toISOString(),
        });

        if (response.type === "ok") {
          toast({
            title: "Success",
            description: `Test for ${campaign.name || campaign.type} campaign sent successfully!`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: response.msg || "Failed to send test campaign",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send test campaign",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch campaigns when session changes (e.g. loading history)
  useEffect(() => {
    if (chatSessionId) {
      fetchSessionCampaigns(chatSessionId).then((fetchedCampaigns) => {
        setCampaigns(fetchedCampaigns);
        if (fetchedCampaigns.length === 0) {
          setShowCampaignSummary(false);
        }
      });
    }
  }, [chatSessionId]);

  return (
    <div className="chat-container">
      <div className="chat-area">
        <header className="page-header">
          <h3>{chatContext.name || ""}</h3>
        </header>
        <MessageList
          fillQuestion={(question) => setQuestionText(question)}
          promptSuggestions={promptSuggestions}
          messages={messages}
          chatBoxRef={chatBoxRef}
          disableSendButton={disableSendButton}
          longProcessing={longProcessing}
          uploadCSVFile={uploadCSVFile}
          onNextClick={handleNextClick}
          showCampaignSummary={showCampaignSummary}
          campaigns={campaigns}
          onCampaignUpdate={handleCampaignUpdate}
          onSendTest={handleSendTest}
          onCampaignBack={handleCampaignBack}
          onScheduleSuccess={handleScheduleSuccess}
        />
        <InputArea
          onSend={handleUserInput}
          onStop={handleStopRequest}
          questionText={questionText}
          setQuestionText={setQuestionText}
          disableSendButton={disableSendButton}
          isStreaming={isStreaming}
        />
        <Input
          ref={fileInputRef}
          key={fileInputKey}
          type="file"
          onChange={handleCSVFileUpload}
          accept=".csv"
          hidden
        />
        {isMobile && (
          <Collapse in={true}>
            <Box className="chat-history-mobile">
              <ChatHistory
                className="right-menu"
                chatSessionId={chatSessionId}
                chatHistory={chatHistory}
                isLoading={isLoading}
              />
            </Box>
          </Collapse>
        )}
      </div>
      {!isMobile && (
        <div className="right-menu">
          <ChatHistory
            chatSessionId={chatSessionId}
            chatHistory={chatHistory}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

export default ChatComponent;
